/**
 * Odpala `callback` DOKŁADNIE RAZ — przy pierwszej realnej interakcji użytkownika
 * (scroll / klik / dotyk / klawisz / ruch wskaźnika), gdy użytkownik OPUSZCZA stronę
 * (visibilitychange → hidden), albo po opcjonalnym fallbackowym timeoucie.
 *
 * Po co: odroczenie ciężkiego third-party (GTM → GA/Ads/Meta/TikTok/Clarity,
 * Segmentify, Sentry Replay) poza krytyczną ścieżkę i poza okno pomiaru CWV.
 * Lighthouse / boty NIE interagują ze stroną, nie chowają jej i nie klikają, więc
 * skrypt nie odpala się w oknie liczenia TBT — a realny użytkownik niemal zawsze
 * scrolluje w 1. sekundach, więc dostaje tracking od razu (w tym: kliknięcie
 * „Akceptuj" w banerze zgody jest interakcją → GTM ładuje się dokładnie wtedy).
 *
 * `fallbackMs`:
 *   - liczba > 0 — dodatkowy timeout łapiący userów, którzy zostają, ale NIE
 *     interagują; UWAGA: zbyt krótki bywa niedeterministyczny w labie (na wolnym
 *     mobile potrafi odpalić się jeszcze w oknie TBT → wahania wyniku).
 *   - `null` / `0` — BEZ timera. Deterministyczny wynik lab (trial nigdy nie
 *     odpali third-party). Zalecane dla ciężkiego third-party przy wymogu stabilnego
 *     wyniku. Realni userzy i tak dostają go przez interakcję lub przy opuszczaniu
 *     strony (visibilitychange). Kompromis: „twardy" bounce bez żadnej interakcji
 *     (nie scrolluje, nie klika, natychmiast wychodzi) może nie zdążyć się otagować.
 *
 * Zwraca funkcję czyszczącą (odpięcie nasłuchów + timera).
 */
export function onFirstInteraction(
  callback: () => void,
  options: { fallbackMs?: number | null } = {},
): () => void {
  // SSR / brak window — no-op (callback dotyczy wyłącznie środowiska klienta).
  if (import.meta.server || typeof window === 'undefined') return () => {}

  const { fallbackMs = 5000 } = options
  const events = [
    'scroll',
    'pointerdown',
    'keydown',
    'touchstart',
    'wheel',
    'pointermove',
  ] as const

  let fired = false
  let timer: ReturnType<typeof setTimeout> | undefined

  function cleanup() {
    if (timer) clearTimeout(timer)
    for (const e of events) window.removeEventListener(e, run)
    document.removeEventListener('visibilitychange', onVisibility)
  }

  function run() {
    if (fired) return
    fired = true
    cleanup()
    callback()
  }

  function onVisibility() {
    // Użytkownik opuszcza / chowa kartę — ostatnia szansa na odpalenie trackingu.
    // Nie odpala się w trakcie triala Lighthouse (karta pozostaje widoczna), więc
    // nie psuje wyniku lab.
    if (document.visibilityState === 'hidden') run()
  }

  for (const e of events) {
    // passive: nie blokujemy scrolla/dotyku; once jako dodatkowe zabezpieczenie.
    window.addEventListener(e, run, { once: true, passive: true })
  }
  document.addEventListener('visibilitychange', onVisibility)

  // Fallback czasowy — wyłączalny (null/0). Guard `fired` czyni go no-opem, gdy
  // interakcja odpali `run` wcześniej.
  if (fallbackMs != null && fallbackMs > 0) timer = setTimeout(run, fallbackMs)

  return cleanup
}
